import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ScreenLayout from './ScreenLayout';
import { Map as MapIcon, Search, Navigation, MapPin, Sparkles } from 'lucide-react';

// Fix for Leaflet marker icons not displaying correctly in production
// This requires importing the marker-icon.png and marker-shadow.png images
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import L from 'leaflet';

// Create custom church icon
const churchIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Default icon settings for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

// Component to recenter the map based on user's location
function LocationMarker() {
  const [position, setPosition] = useState(null);
  const map = useMap();

  useEffect(() => {
    map.locate().on('locationfound', function (e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    }).on('locationerror', function (e) {
      console.error('Error getting location:', e.message);
    });
  }, [map]);

  return position === null ? null : (
    <Marker position={position}>
      <Popup>You are here</Popup>
    </Marker>
  );
}

// Main component
const WorshipLocator = () => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRadius, setSearchRadius] = useState(5); // km
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([40.7128, -74.0060]); // Default to NYC
  const [mapZoom] = useState(13);
  const [searchType, setSearchType] = useState('church'); // Default to churches

  // Types of worship places to search for
  const worshipTypes = [
    { id: 'church', label: 'Churches', value: 'place_of_worship' },
    { id: 'synagogue', label: 'Synagogues', value: 'place_of_worship' },
    { id: 'mosque', label: 'Mosques', value: 'place_of_worship' },
    { id: 'temple', label: 'Temples', value: 'place_of_worship' }
  ];

  // Helper function to search for places of worship using Overpass API
  const searchPlacesOfWorship = async (lat, lon, radius, type) => {
    const amenity = type === 'church' ? 'place_of_worship' : type;
    const religion = type === 'church' ? 'christian' :
      type === 'synagogue' ? 'jewish' :
        type === 'mosque' ? 'muslim' :
          type === 'temple' ? 'buddhist,hindu,taoist' : '';

    // Build the Overpass query
    let query = `
      [out:json][timeout:25];
      (
        node["amenity"="${amenity}"]${religion ? `["religion"~"${religion}"]` : ''}(around:${radius * 1000},${lat},${lon});
        way["amenity"="${amenity}"]${religion ? `["religion"~"${religion}"]` : ''}(around:${radius * 1000},${lat},${lon});
        relation["amenity"="${amenity}"]${religion ? `["religion"~"${religion}"]` : ''}(around:${radius * 1000},${lat},${lon});
      );
      out center;
    `;

    try {
      setLoading(true);
      const response = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`);
      const data = await response.json();

      // Process the results
      const placesData = data.elements.map(element => {
        // Handle different element types (node, way, relation)
        const placeLat = element.lat || element.center?.lat;
        const placeLon = element.lon || element.center?.lon;

        return {
          id: element.id,
          name: element.tags.name || 'Unnamed Place of Worship',
          type: element.tags.religion || 'Unknown',
          denomination: element.tags.denomination || '',
          lat: placeLat,
          lon: placeLon,
          address: element.tags['addr:street']
            ? `${element.tags['addr:housenumber'] || ''} ${element.tags['addr:street']}, ${element.tags['addr:city'] || ''}, ${element.tags['addr:postcode'] || ''}`
            : 'No address available'
        };
      });

      setPlaces(placesData);
    } catch (error) {
      console.error('Error fetching places of worship:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get user's location when component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lon: longitude });
          setMapCenter([latitude, longitude]);
          searchPlacesOfWorship(latitude, longitude, searchRadius, searchType);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (userLocation) {
      searchPlacesOfWorship(userLocation.lat, userLocation.lon, searchRadius, searchType);
    }
  };

  const directionsUrl = (place) =>
    `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lon}`;

  return (
    <ScreenLayout title="Worship Locator" icon={MapIcon} tone="copper" eyebrow="Find community">
      <section className="locator-hero">
        <p className="shared-kicker"><Sparkles size={14} /> Gather together</p>
        <h1>Find a place to worship near you.</h1>
        <p>Discover churches and other houses of worship close by, then get directions in a tap.</p>
      </section>

      <section className="locator-controls">
        <h2>Search near your location</h2>
        <div className="locator-fields">
          <label className="locator-field">
            <span>Type of place</span>
            <select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
              {worshipTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </label>

          <label className="locator-field">
            <span>Search radius</span>
            <select value={searchRadius} onChange={(e) => setSearchRadius(Number(e.target.value))}>
              <option value="1">1 km</option>
              <option value="2">2 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="20">20 km</option>
            </select>
          </label>

          <button className="locator-search-button" onClick={handleSearch} disabled={loading || !userLocation}>
            <Search size={18} />
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        {!userLocation && (
          <p className="locator-hint">
            <MapPin size={14} /> Allow location access to search around you.
          </p>
        )}
      </section>

      <div className="locator-layout">
        <div className="locator-map">
          <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker />
            {places.map((place) => (
              <Marker key={place.id} position={[place.lat, place.lon]} icon={churchIcon}>
                <Popup>
                  <strong>{place.name}</strong>
                  <br />
                  <span>{place.type}{place.denomination ? ` (${place.denomination})` : ''}</span>
                  <br />
                  <span>{place.address}</span>
                  <br />
                  <a href={directionsUrl(place)} target="_blank" rel="noopener noreferrer">Directions</a>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <aside className="locator-results">
          <header>Places found <span>{places.length}</span></header>
          <div className="locator-results-body">
            {loading ? (
              <div className="locator-spinner"><span /></div>
            ) : !places.length ? (
              <div className="locator-empty">
                <MapPin size={26} />
                <p>No places found here yet. Try widening the search radius.</p>
              </div>
            ) : (
              <ul>
                {places.map((place) => (
                  <li className="locator-result" key={place.id}>
                    <h4>{place.name}</h4>
                    <p className="locator-result-type">{place.type}{place.denomination ? ` (${place.denomination})` : ''}</p>
                    <p className="locator-result-address">{place.address}</p>
                    <a href={directionsUrl(place)} target="_blank" rel="noopener noreferrer">
                      <Navigation size={14} /> Directions
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </ScreenLayout>
  );
};

export default WorshipLocator;
